import React, {useContext, useEffect, useState} from 'react';
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import userService from "../../../../../services/web/user-service.js";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import {addDays, endOfDay, isAfter, isWithinInterval, startOfDay} from "date-fns";
import {FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";
import {MaterialMovementsContext} from "../material-movements.context.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialAlertDate = {
    expirationCnh: {divClass: "", icon: ""},
    carryDueDate: {divClass: "", icon: ""},
    shotDueDate: {divClass: "", icon: ""},
    psychoDueDate: {divClass: "", icon: ""},
}

const Requester = () => {
    const {userSelected, setUserSelected, setIsLoading} = useContext(MaterialMovementsContext)
    const [alertDate, setAlertDate] = useState(initialAlertDate);

    const {data: users, isLoading: isLoadingUsers} = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.optionsWithoutSystem(),
    });

    const {data: user, status, isLoading: isLoadingUser} = useQuery({
        queryKey: ['user', userSelected],
        queryFn: async () => {
            const {id, name, namewar, expirationCnh, carryDueDate, shotDueDate, psychoDueDate, categoryCnh} = await userService.conductorDocumentStatus(userSelected?.value)

            handleCondition({expirationCnh, carryDueDate, shotDueDate, psychoDueDate});

            return {
                id, name, namewar,
                expirationCnh: projectUtils.formatDate(expirationCnh, "DD/MM/YYYY"),
                carryDueDate: projectUtils.formatDate(carryDueDate, "DD/MM/YYYY"),
                shotDueDate: projectUtils.formatDate(shotDueDate, "DD/MM/YYYY"),
                psychoDueDate: projectUtils.formatDate(psychoDueDate, "DD/MM/YYYY"),
                categoryCnh: categoryCnh?.join(", ")
            }
        },
        enabled: !!userSelected
    });

    const handleCondition = (documents) => {
        const fieldsInfo = ['expirationCnh', 'carryDueDate', 'shotDueDate', 'psychoDueDate'];

        const alertDatePrepared = {}

        for (const field of fieldsInfo) {
            const todayStart = startOfDay(new Date());
            const givenDate = documents[field]
            const givenDateEnd = givenDate ? endOfDay(givenDate) : null
            const almostDate = addDays(todayStart, 45);

            let fieldI18n = ""
            switch (field) {
                case 'expirationCnh':
                    fieldI18n = 'CNH';
                    break
                case 'carryDueDate':
                    fieldI18n = 'Porte de arma';
                    break
                case 'shotDueDate':
                    fieldI18n = 'Exame de tiro';
                    break
                case 'psychoDueDate':
                    fieldI18n = 'Exame psicológico';
                    break
            }

            let divClass = "";
            let icon = "";
            if (givenDateEnd) {
                if (!isAfter(givenDateEnd, todayStart)) {
                    divClass = "!text-[#dc4c64] !border-[#dc4c64]";
                    icon = <FaTriangleExclamation color={"dc4c64"} className={"absolute top-[9px] right-2.5"} title={fieldI18n + (field === "expirationCnh" ? " vencida" : " vencido")}/>
                } else if (isWithinInterval(givenDateEnd, {start: todayStart, end: almostDate})) {
                    divClass = "!text-[#FC6D00] !border-[#FC6D00]";
                    icon = <FaCircleExclamation color={"#FC6D00"} className={"absolute top-[9px] right-2.5"} title={fieldI18n + " a vencer"}/>
                }
            } else {
                divClass = "!text-[#dc4c64] !border-[#dc4c64]";
                icon = <FaTriangleExclamation color={"dc4c64"} className={"absolute top-[9px] right-2.5"} title={"Não possui " + fieldI18n}/>

            }

            alertDatePrepared[field] = {divClass, icon}
        }

        setAlertDate(alertDatePrepared);
    }

    useEffect(() => {
        setIsLoading(isLoadingUsers || isLoadingUser)
    }, [isLoadingUsers, isLoadingUser]);

    return (
        <div className="col-span-7">
            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                    <SelectComponent
                        id="user"
                        name="user"
                        value={userSelected}
                        options={users || []}
                        label={"Solicitante *"}
                        onChange={(selected) => setUserSelected(selected)}
                    />
                </div>

                <div className="col-span-3">
                    <div className={"h-[137px] p-1 bg-white border border-[#e0e0e0] rounded shadow-sm"}>
                        { !user && <img className={"h-full w-full object-contain"} src={getAssetPath("img/user.png")}/>}
                        {user && <img className={"h-full w-full object-contain"} src={API_URL + `/user/getImage/${user.id}`}/>}
                    </div>
                </div>
                <div className="col-span-9">
                    <div className="grid grid-cols-12 gap-4">
                        <div className={"col-span-6"}>
                            <InputForm id="name" labelClass={"!bg-white"} value={user?.name || ""} onChange={() => {}} label="Nome" inputClass={"!bg-white"} disabled/>
                        </div>
                        <div className={"col-span-6"}>
                            <InputForm id="namewar" value={user?.namewar || ""} labelClass={"!bg-white"} onChange={() => {}} label="Nome de guerra" inputClass={"!bg-white"} disabled/>
                        </div>
                        <div className={"col-span-6 relative"}>
                            <InputForm
                                id="expirationCnh"
                                value={user?.expirationCnh || ""}
                                onChange={() => {}}
                                label="CNH"
                                labelClass={alertDate.expirationCnh.divClass + " !bg-white"}
                                classNameWrapper={alertDate.expirationCnh.divClass}
                                inputClass={"!bg-white"}
                                disabled/>
                            {user?.categoryCnh && (
                                <span
                                    className={`absolute text-[12px] top-[9px] bg-[#e8f0fe] text-[#1967D2] rounded px-1 ${alertDate.expirationCnh.icon ? "right-8.5" : "right-2.5"}`}
                                    title={`Categoria: ${user.categoryCnh}`}
                                >
                                    Cat. {user.categoryCnh}
                                </span>
                            )}
                            {alertDate.expirationCnh && (<>{alertDate.expirationCnh.icon}</>)}
                        </div>
                        <div className={"col-span-6 relative"}>
                            <InputForm
                                id="carryDueDate"
                                value={user?.carryDueDate || ""}
                                onChange={() => {}}
                                label="Porte de arma"
                                labelClass={alertDate.carryDueDate.divClass + " !bg-white"}
                                classNameWrapper={alertDate.carryDueDate.divClass}
                                inputClass={"!bg-white"}
                                disabled/>
                            {alertDate.carryDueDate && (<>{alertDate.carryDueDate.icon}</>)}
                        </div>
                        <div className={"col-span-6 relative"}>
                            <InputForm
                                id="shotDueDate"
                                value={user?.shotDueDate || ""}
                                onChange={() => {}}
                                label="Exame de tiro"
                                labelClass={alertDate.shotDueDate.divClass + " !bg-white"}
                                classNameWrapper={alertDate.shotDueDate.divClass}
                                inputClass={"!bg-white"}
                                disabled/>
                            {alertDate.shotDueDate && (<>{alertDate.shotDueDate.icon}</>)}
                        </div>
                        <div className={"col-span-6 relative"}>
                            <InputForm
                                id="psychoDueDate"
                                value={user?.psychoDueDate || ""}
                                onChange={() => {}}
                                label="Exame psicológico"
                                labelClass={alertDate.psychoDueDate.divClass + " !bg-white"}
                                classNameWrapper={alertDate.psychoDueDate.divClass}
                                inputClass={"!bg-white"}
                                disabled/>
                            {alertDate.psychoDueDate && (<>{alertDate.psychoDueDate.icon}</>)}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Requester;
